import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import auth, { FirebaseAuthTypes } from '@react-native-firebase/auth';
import type { V2User } from '../types/v2';
import * as authService from '../services/auth';
import { BiometryType } from '../services/biometrics';
import { notificationService } from '../services/notifications';
import { badgeManager } from '../services/badge';
import { v2Impersonate, v2StopImpersonation, v2GetMe } from '../services/v2/api';
import {isV2ApiError} from '../services/v2/errors';
import { activatePrefill } from '../services/loginPrefill';
import { startSession, endSession, markSessionForEnd } from '../services/sessionTracker';

interface AuthState {
  user: V2User | null;
  firebaseUser: FirebaseAuthTypes.User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  biometryType: BiometryType;
  biometricsEnabled: boolean;

  // Impersonation state — not persisted by the app: on a cold start it is
  // read back from /auth/me, which the client's Firebase session answers.
  isImpersonating: boolean;
  impersonatorLogin: string | null;
  impSessionID: string | null;
}

interface AuthContextType extends AuthState {
  login: (loginName: string, password: string) => Promise<void>;
  loginWithBiometrics: () => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  handleSessionExpired: (message?: string) => Promise<void>;
  enableBiometrics: () => Promise<boolean>;
  disableBiometrics: () => Promise<boolean>;
  refreshBiometricsStatus: () => Promise<void>;

  // Impersonation — super-user only
  impersonate: (targetLogin: string, callerPassword: string, reason?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;

  /**
   * Re-fetch the current user from /auth/me and merge into state. Useful when
   * something out-of-band may have changed (e.g. a DBA flipped is_super_user)
   * and the cached login-response data is now stale. Best-effort: failures
   * are logged but do not change state, so the UI doesn't flicker.
   */
  refreshUser: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  firebaseUser: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  biometryType: null,
  biometricsEnabled: false,
  isImpersonating: false,
  impersonatorLogin: null,
  impSessionID: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  // Read by logout, which must know whether it is leaving an impersonation.
  const isImpersonatingRef = useRef(false);
  isImpersonatingRef.current = state.isImpersonating;
  // True while "Login as user" or its Stop swaps the Firebase session: the
  // sign-in that follows the swap is not a new device session. Each swap used
  // to open one — refused for the client (403), and a stray second row for
  // the super-user on Stop, whose first row was then never closed.
  const identitySwitchRef = useRef(false);
  // The uid a swap signed in, for when Firebase reports that sign-in only
  // after the swap has returned. Cleared by an explicit login or logout.
  const switchedToUidRef = useRef<string | null>(null);
  const markSwitchedTo = () => { switchedToUidRef.current = auth().currentUser?.uid ?? null; };

  // Try to restore session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Initialize notification service early
        await notificationService.initialize();

        // Check biometrics availability
        const biometryType = await authService.getSupportedBiometryType();
        const biometricsEnabled = await authService.isBiometricLoginEnabled();

        const result = await authService.restoreSession();
        if (result) {
          // A restart during "Login as user" comes back in the client's
          // Firebase session, and the server still treats it as one: /auth/me
          // says so. Take it from there — otherwise the banner and its Stop
          // vanish and the audit row stays open with no way to close it.
          const restoredImpersonation = result.user.is_impersonating === true;
          setState({
            user: result.user,
            firebaseUser: result.firebaseUser,
            isLoading: false,
            isAuthenticated: true,
            error: null,
            biometryType,
            biometricsEnabled,
            isImpersonating: restoredImpersonation,
            impersonatorLogin: restoredImpersonation ? result.user.impersonator_login ?? null : null,
            // /auth/me does not carry the session id; Stop does not need it —
            // the server finds the session from the token.
            impSessionID: null,
          });

          // Register FCM token after successful session restore — but only
          // if the server-derived capability allows it. Under impersonation
          // can_register_fcm is false (would bind super-user's device to the
          // target user's push subscriptions).
          if (result.user.capabilities?.can_register_fcm !== false) {
            notificationService.registerToken().catch(err => {
              console.warn('Failed to register FCM token on session restore:', err);
            });
          }

          // Refresh badge count from backend
          badgeManager.refreshFromBackend().catch(err => {
            console.warn('Failed to refresh badge on session restore:', err);
          });
        } else {
          setState({
            ...initialState,
            isLoading: false,
            biometryType,
            biometricsEnabled,
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        const biometryType = await authService.getSupportedBiometryType();
        const biometricsEnabled = await authService.isBiometricLoginEnabled();
        setState({
          ...initialState,
          isLoading: false,
          biometryType,
          biometricsEnabled,
        });
      }
    };

    initializeAuth();
  }, []);

  // Tracks the previous firebaseUser uid so we only fire startSession on the
  // null → user transition, not on every re-emission of the same auth state.
  // Covers ALL entry paths the user can take into the app:
  //   1. Explicit username/password login (login()).
  //   2. Explicit biometric login from the login screen (loginWithBiometrics()).
  //   3. Cold-start with cached Firebase session (Keychain auto-unlock via
  //      FaceID/TouchID without the user ever seeing the login screen) —
  //      neither (1) nor (2) runs in this case; only onAuthStateChanged fires.
  const prevFirebaseUidRef = useRef<string | null>(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(firebaseUser => {
      setState(prev => ({
        ...prev,
        firebaseUser,
      }));

      const newUid = firebaseUser ? firebaseUser.uid : null;
      const prevUid = prevFirebaseUidRef.current;
      prevFirebaseUidRef.current = newUid;

      // Only fire on null → user transition. Suppresses duplicates when
      // Firebase re-emits the same auth state (token refresh, etc.) and avoids
      // creating a session row when the listener attaches for the first time
      // on a user who's already signed in but has no fresh "entry" event yet.
      const fromSwap = identitySwitchRef.current || (newUid != null && newUid === switchedToUidRef.current);
      if (newUid && !prevUid && !fromSwap) {
        startSession(newUid);
      }
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async (loginName: string, password: string) => {
    // A sign-in of its own opens a session, whoever was switched to before.
    switchedToUidRef.current = null;
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    console.log('[AuthContext.login] Attempting login with:', { loginName, passwordLength: password.length });

    try {
      const result = await authService.login(loginName, password);
      console.log('[AuthContext.login] Success:', { user: result.user?.login, hasToken: !!result.firebaseUser });
      const biometryType = await authService.getSupportedBiometryType();
      const biometricsEnabled = await authService.isBiometricLoginEnabled();

      setState(prev => ({
        ...prev,
        user: result.user,
        firebaseUser: result.firebaseUser,
        isLoading: false,
        isAuthenticated: true,
        error: null,
        biometryType,
        biometricsEnabled,
      }));

      // Register FCM token after successful login — only if the server allows
      // it for this session (always true on a fresh login; defensive check).
      if (result.user.capabilities?.can_register_fcm !== false) {
        notificationService.registerToken().catch(err => {
          console.warn('Failed to register FCM token on login:', err);
        });
      }

      // Session row is opened via the onAuthStateChanged listener above —
      // doing it here would duplicate (Firebase fires the state change as
      // part of signInWithEmailAndPassword).

      // Refresh badge count from backend
      badgeManager.refreshFromBackend().catch(err => {
        console.warn('Failed to refresh badge on login:', err);
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      const isExpectedRejection =
        isV2ApiError(error) &&
        (error.code === 'invalid_credentials' ||
          error.code === 'validation_error');
      const errorDetails = {
        errorMessage,
        errorType:
          error instanceof Error ? error.constructor.name : typeof error,
      };
      if (isExpectedRejection) {
        console.log('[AuthContext.login] Credentials rejected:', errorDetails);
      } else {
        console.error('[AuthContext.login] Error:', errorDetails);
      }
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const loginWithBiometrics = useCallback(async (): Promise<boolean> => {
    // A sign-in of its own opens a session, whoever was switched to before.
    switchedToUidRef.current = null;
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await authService.loginWithBiometrics();
      if (result) {
        setState(prev => ({
          ...prev,
          user: result.user,
          firebaseUser: result.firebaseUser,
          isLoading: false,
          isAuthenticated: true,
          error: null,
        }));

        // Register FCM token after successful biometric login — only if the
        // server allows it for this session.
        if (result.user.capabilities?.can_register_fcm !== false) {
          notificationService.registerToken().catch(err => {
            console.warn('Failed to register FCM token on biometric login:', err);
          });
        }

        // Session row is opened via the onAuthStateChanged listener above
        // (see comment near prevFirebaseUidRef) — doing it here would dup.

        // Refresh badge count from backend
        badgeManager.refreshFromBackend().catch(err => {
          console.warn('Failed to refresh badge on biometric login:', err);
        });

        return true;
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
        }));
        return false;
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
      }));
      return false;
    }
  }, []);

  const enableBiometrics = useCallback(async (): Promise<boolean> => {
    const success = await authService.enableBiometricLogin();
    if (success) {
      setState(prev => ({ ...prev, biometricsEnabled: true }));
    }
    return success;
  }, []);

  const disableBiometrics = useCallback(async (): Promise<boolean> => {
    const success = await authService.disableBiometricLogin();
    if (success) {
      setState(prev => ({ ...prev, biometricsEnabled: false }));
    }
    return success;
  }, []);

  const refreshBiometricsStatus = useCallback(async () => {
    const biometryType = await authService.getSupportedBiometryType();
    const biometricsEnabled = await authService.isBiometricLoginEnabled();
    setState(prev => ({ ...prev, biometryType, biometricsEnabled }));
  }, []);

  const logout = useCallback(async () => {
    activatePrefill();
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Unregister FCM token before logging out
      await notificationService.unregisterToken().catch(err => {
        console.warn('Failed to unregister FCM token on logout:', err);
      });

      // Signing out during "Login as user": end the impersonation first. That
      // closes its audit row, and gives back the super-user's token — the
      // session row is theirs, and the server refuses to close it with the
      // client's (403), after which the row used to be forgotten.
      if (isImpersonatingRef.current) {
        identitySwitchRef.current = true;
        try {
          const res = await v2StopImpersonation();
          await auth().signOut();
          await auth().signInWithCustomToken(res.firebase_token);
          markSwitchedTo();
        } catch (err) {
          console.warn('Failed to end impersonation on logout:', err);
        } finally {
          identitySwitchRef.current = false;
        }
      }

      // Close the audit-log session row. Done BEFORE authService.logout()
      // because /sessions/:id/end needs a still-valid Firebase token.
      await endSession('logout').catch(err => {
        console.warn('Failed to end session on logout:', err);
      });

      // Clear app icon badge
      await badgeManager.clearBadge().catch(err => {
        console.warn('Failed to clear badge on logout:', err);
      });

      switchedToUidRef.current = null;
      await authService.logout();
      // Re-check biometrics status after logout (credentials are cleared)
      const biometryType = await authService.getSupportedBiometryType();
      const biometricsEnabled = await authService.isBiometricLoginEnabled();
      setState({
        ...initialState,
        isLoading: false,
        biometryType,
        biometricsEnabled,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if there's an error (biometrics cleared)
      const biometryType = await authService.getSupportedBiometryType();
      const biometricsEnabled = await authService.isBiometricLoginEnabled();
      setState({
        ...initialState,
        isLoading: false,
        biometryType,
        biometricsEnabled,
      });
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const handleSessionExpired = useCallback(async (message?: string) => {
    // Clear credentials and log out
    try {
      // The device token is deliberately kept. An expired session is not a
      // refusal of notifications — the client signs back in within seconds,
      // and deleting the token here left them with none until the app
      // happened to register a new one. That is how a whole stand ended up
      // with no tokens after everyone was signed out at once.
      //
      // Leaking pushes to whoever logs in next is not a risk: registering a
      // token claims it, and the server drops that same token from every
      // other user as it does so.
      //
      // The session row is closed as expired. The server usually refuses the
      // token by now; the row is then kept and closed on the user's next
      // sign-in (sessionTracker). It used to stay open until the stale sweep.
      //
      // Under "Login as user" the token is the client's and the row the
      // super-user's: the server would refuse it (403). It is only marked,
      // to be closed as expired when the super-user signs in again.
      if (isImpersonatingRef.current) {
        await markSessionForEnd('expired').catch(() => {});
      } else {
        await endSession('expired').catch(() => {});
      }
      switchedToUidRef.current = null;
      await authService.logout();
    } catch (e) {
      console.warn('Logout during session expiry failed:', e);
    }
    // Re-check biometrics status (credentials still exist)
    const biometryType = await authService.getSupportedBiometryType();
    const biometricsEnabled = await authService.isBiometricLoginEnabled();
    // Set error message and mark as not authenticated
    setState({
      ...initialState,
      isLoading: false,
      error: message || 'Session expired. Please login again.',
      biometryType,
      biometricsEnabled,
    });
  }, []);

  /**
   * Start impersonation: super-user -> target user.
   * Requires caller's password (step-up). Replaces the active Firebase session
   * with one for the target. After a restart the state is read back from /auth/me.
   */
  const impersonate = useCallback(
    async (targetLogin: string, callerPassword: string, reason?: string) => {
      // 1. Call backend — authorized with the current super-user's Firebase token.
      const res = await v2Impersonate(targetLogin, callerPassword, reason);

      // 2. Swap the active Firebase session: sign out super, sign in target with custom token.
      //    (iOS/Android Firebase SDK: this replaces auth.currentUser with the target's UID.)
      //    The super-user's own session row stays open: it is the same
      //    device session, and the impersonation has its own audit row.
      identitySwitchRef.current = true;
      try {
        await auth().signOut();
        await auth().signInWithCustomToken(res.firebase_token);
        markSwitchedTo();
      } finally {
        identitySwitchRef.current = false;
      }

      // 3. Fetch profile again — now the backend sees the imp claims and fills
      //    is_impersonating + impersonator_login into UserDTO.
      const targetUser = await v2GetMe();

      setState(prev => ({
        ...prev,
        user: targetUser,
        firebaseUser: auth().currentUser,
        isAuthenticated: true,
        isImpersonating: true,
        impersonatorLogin: res.impersonator_login,
        impSessionID: res.session_id,
        error: null,
      }));
    },
    [],
  );

  /**
   * Re-fetch the current user from /auth/me and merge it into state. We avoid
   * touching isAuthenticated / firebaseUser / loading flags so callers (e.g. a
   * useEffect on ProfileScreen mount) never accidentally trigger a re-render
   * cascade. If the request fails — for example, transient network — we keep
   * whatever we already had.
   */
  const refreshUser = useCallback(async () => {
    try {
      const fresh = await v2GetMe();
      setState(prev => ({ ...prev, user: fresh }));
    } catch (err) {
      console.warn('[AuthContext.refreshUser] failed:', err);
    }
  }, []);

  /**
   * Stop impersonation: closes server-side audit row and restores super-user's session.
   */
  const stopImpersonation = useCallback(async () => {
    try {
      const res = await v2StopImpersonation();
      // Swap back to the super-user session — the one whose row is still open.
      identitySwitchRef.current = true;
      try {
        await auth().signOut();
        await auth().signInWithCustomToken(res.firebase_token);
        markSwitchedTo();
      } finally {
        identitySwitchRef.current = false;
      }
      const superUser = await v2GetMe();

      setState(prev => ({
        ...prev,
        user: superUser,
        firebaseUser: auth().currentUser,
        isAuthenticated: true,
        isImpersonating: false,
        impersonatorLogin: null,
        impSessionID: null,
        error: null,
      }));
    } catch (err) {
      console.error('[AuthContext.stopImpersonation] Error:', err);
      // Fall back to a full logout so we never leave the app stuck in imp state.
      await logout();
      throw err;
    }
  }, [logout]);

  const value: AuthContextType = {
    ...state,
    login,
    loginWithBiometrics,
    logout,
    clearError,
    handleSessionExpired,
    enableBiometrics,
    disableBiometrics,
    refreshBiometricsStatus,
    impersonate,
    stopImpersonation,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

